/* @flow */
import * as React from 'react';
import cx from 'classnames';
import { Container } from 'flux/utils';

import Avatar from 'components/Avatar';
import Button from 'components/ButtonDeprecated';
import Glyph from 'components/Glyph';
import GroupChatAvatar from 'chat_v2/components/GroupChatAvatar';
import ErrorReporter from 'app/components/ErrorReporter';
import Link from 'components/Link';
import Text from 'components/Text';
import Timestamp from 'components/Timestamp';
import UnreadStore from 'stores/UnreadStore';
import UserAvatar from 'users/components/UserAvatar';

import type { ChatTypeType } from 'entities/Chat';

import 'chat_v2/components/styles/ChatListItem.scss';

type Props = {
  url: string,
  name: string,
  date?: ?string,
  groupAvatar?: string,
  otherMember?: *,
  chatType?: ChatTypeType,
  preview?: string,
  streamUuid?: string,
  newChat?: boolean,
  newChatOnClose?: Function,
};

type State = {
  unreadCount: number,
};

class StreamSectionListItem extends React.Component<Props, State> {
  static getStores() {
    return [UnreadStore];
  }

  static calculateState(prevState: State, props: Props) {
    if (!props.streamUuid) {
      return { unreadCount: 0 };
    }
    return {
      unreadCount: UnreadStore.getUnreadCount(props.streamUuid),
    };
  }

  renderAvatar = () => {
    const { groupAvatar, otherMember, chatType } = this.props;

    if (!(groupAvatar || chatType)) {
      return (
        <ErrorReporter error="must pass either a groupAvatar or a chatType to StreamSectionListItem" />
      );
    }

    if (groupAvatar) {
      return <Avatar image={groupAvatar} className="ChatListItem-announcementBadge" />;
    }

    if (chatType === 'individual') {
      return <UserAvatar user={otherMember} size="medium" disableBadgeHover />;
    }

    if (chatType === 'group') {
      return <GroupChatAvatar size="medium" />;
    }

    return null;
  };

  render() {
    const { date, url, preview, name, newChat } = this.props;

    if (newChat) {
      return (
        <li className="ChatListItemee">
          <Link
            to={url}
            activeClassName="ChatListItem-selected"
            className={cx('ChatListItem-link', 'ChatListItem-newChat')}>
            <div className="ChatListItem-content">
              <div className="ChatListItem-content-droplet">{this.renderAvatar()}</div>
              <div className="ChatListItem-content-body">
                <div className="ChatListItem-content-body-inner">
                  <Text className="ChatListItem-name" truncate>
                    {name}
                  </Text>
                  {date && (
                    <Timestamp className="ChatListItem-date" date={new Date(date)} format="short" />
                  )}
                </div>
                <Text className="ChatListItem-snippet" truncate>
                  Send your first message
                </Text>
                <hr className="ChatListItem-content-body-divider" />
              </div>
              <Button
                className="ChatListItem-closeButton"
                level="text"
                onClick={this.props.newChatOnClose}
                aria-label="Close">
                <Glyph name="close" size="xsmall" solo />
              </Button>
            </div>
          </Link>
        </li>
      );
    }

    return (
      <li className="ChatListItem" data-test-selector={`stream-section-list-item-${name}`}>
        <Link
          to={url}
          activeClassName="ChatListItem-selected"
          className={cx('ChatListItem-link', 'ChatListItem-link-experiment', {
            'ChatListItem-hasUnread': this.state.unreadCount > 0,
          })}>
          <div className="ChatListItem-content">
            <div className="ChatListItem-content-droplet">{this.renderAvatar()}</div>
            <div className="ChatListItem-content-body">
              <div className="ChatListItem-content-body-inner">
                <Text className="ChatListItem-name" truncate>
                  {name}
                </Text>
                {date && (
                  <Timestamp className="ChatListItem-date" date={new Date(date)} format="short" />
                )}
              </div>
              <Text className="ChatListItem-snippet" truncate>
                {preview || 'Send your first message'}
              </Text>
              <hr className="ChatListItem-content-body-divider" />
            </div>
          </div>
        </Link>
      </li>
    );
  }
}

export { StreamSectionListItem };

export default Container.create(StreamSectionListItem, { withProps: true, pure: false });
